import { useCallback, useEffect, useState } from "react";
import { BooleanParam, StringParam, useQueryParams } from "use-query-params";
import styled from "styled-components/macro";
import { HelpDialog } from "../../../SharedPages/OrganizationPage/ProductsList/CreatePimProductPage/CreateFromUploads/HelpDialog";
import {
  H6Bold,
  SmallSectionHeaderRegular,
} from "../../../../components/Typography/Typography";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { InfoIcon } from "../../../../components/Icons/Icons";
import { DownloadStage } from "./DownloadStage";
import { UploadStage } from "./UploadStage";
import { PreviousCustomersUploadTable } from "./PreviousCustomersUploadTable";
import { screenSize } from "../../../../theme";
import { IconButtonMedium } from "../../../../components/Buttons/Buttons";
import { spacingTokens } from "../../../../designTokens";
import { useMdmEntityDisplayNames } from "../../../../util/mdmEntityDisplayNames";
import {
  EntityType,
  BulkUploadCustomerType,
} from "../../../../types/types.MDM";

export function bulkUploadEntityType(
  customerType: BulkUploadCustomerType
): EntityType {
  return customerType === "customers" ? "customer" : "distributor";
}

const UploadContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const MainContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_5xl")};
`;

const DownloadStageWrapper = styled.div`
  max-width: 512px;
`;

const GuideContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: flex-end;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const GuideContainerDesktop = styled(GuideContainer)`
  @media ${screenSize.medium} {
    display: none;
  }
`;

const GuideContainerMobile = styled(GuideContainer)`
  @media ${screenSize.medium} {
    display: flex;
  }
  @media screen and (min-width: 769px) {
    display: none;
  }
`;

const guidelineOverview = (
  t: TFunction,
  labels: { singular: string; plural: string }
) => [
  t(
    "The CSV contains the required headers for {{entity}} creation. Fill the required details beneath the respective headers column.",
    { entity: labels.singular }
  ),
  t("Once the CSV is updated, upload the file to create {{entities}}.", {
    entities: labels.plural,
  }),
];

const csvGuidelines = (
  t: TFunction,
  labels: { singular: string; plural: string }
) => [
  t("Do not modify the header row."),
  t(
    "The address line 1, address line 2, state, city, country and zip code fields should belong to {{entities}} headquarter address.",
    { entities: labels.plural }
  ),
  t(
    "Enter the alpha-2 code of the respective country under country column. Example:- US for United States of America, BR for Brazil etc."
  ),
  t(
    "The first name, last name, email id, country code and phone number should belong to the {{entities}} primary contact.",
    { entities: labels.plural }
  ),
  t("The preferred language should be storefront supported language."),
  t(
    "Enter the three letter alphabetic codes for preferred currency. Example:- USD for US Dollar, CAD for Canadian Dollar, EUR for Euro etc."
  ),
  t("The preferred currency should be storefront supported currency."),
];

export const BulkUploadCustomers = ({
  customer_type,
}: {
  customer_type: BulkUploadCustomerType;
}) => {
  const [stage, setStage] = useState<"download" | "upload">("download");
  const [showGuide, setShowGuide] = useState<boolean>(false);
  const [query, setQuery] = useQueryParams({
    uploadNumber: StringParam,
    shouldMutate: BooleanParam,
  });
  const [uploadNumber, setUploadNumber] = useState<string>();
  const { t } = useTranslation();
  const { getLabel } = useMdmEntityDisplayNames();
  const entityType = bulkUploadEntityType(customer_type);
  const entityLabels = {
    singular: getLabel(entityType, "singular"),
    plural: getLabel(entityType, "plural"),
  };

  const updateStage = useCallback(
    (
      stage: "download" | "upload",
      uploadNumber?: string,
      shouldMutate: boolean = false
    ) => {
      setStage(stage);
      if (uploadNumber) {
        setUploadNumber(uploadNumber);
      }
      setQuery({
        uploadNumber,
        shouldMutate,
      });
    },
    [setStage, setQuery, setUploadNumber]
  );

  useEffect(() => {
    if (query.uploadNumber && !uploadNumber) {
      updateStage("upload", query.uploadNumber);
    }
  }, [query.uploadNumber, updateStage, uploadNumber]);

  return (
    <MainContentContainer>
      <UploadContainer>
        {stage === "download" ? (
          <>
            <DownloadStageWrapper>
              <DownloadStage
                updateStage={updateStage}
                customer_type={customer_type}
              />
            </DownloadStageWrapper>
          </>
        ) : (
          <UploadStage
            updateStage={updateStage}
            customer_type={customer_type}
          />
        )}
        <GuideContainerDesktop
          style={{ alignSelf: "flex-start", gap: spacingTokens.spacing_xs }}
        >
          <HelpDialog
            show={showGuide}
            setShow={setShowGuide}
            overview={(t) => guidelineOverview(t, entityLabels)}
            guidelines={(t) => csvGuidelines(t, entityLabels)}
            guidelineHeader={t("Guidelines for Updating Your Files")}
          />
          <SmallSectionHeaderRegular style={{ margin: 0 }}>
            {t("Need help getting started?")}
          </SmallSectionHeaderRegular>
          <div
            onClick={() => setShowGuide(true)}
            style={{
              display: "flex",
              gap: spacingTokens.spacing_xs,
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <InfoIcon />
            <H6Bold>{t("View Guide")}</H6Bold>
          </div>
        </GuideContainerDesktop>
        <GuideContainerMobile
          style={{ alignSelf: "flex-start", gap: spacingTokens.spacing_xs }}
        >
          <HelpDialog
            show={showGuide}
            setShow={setShowGuide}
            overview={(t) => guidelineOverview(t, entityLabels)}
            guidelines={(t) => csvGuidelines(t, entityLabels)}
            guidelineHeader={t("Guidelines for Updating Your Files")}
          />
          <IconButtonMedium
            datatip={t("View Guide")}
            datafor="view-guide-tooltip"
            onClick={() => setShowGuide(true)}
          >
            <InfoIcon />
          </IconButtonMedium>
        </GuideContainerMobile>
      </UploadContainer>
      {stage === "download" && (
        <PreviousCustomersUploadTable customer_type={customer_type} />
      )}
    </MainContentContainer>
  );
};
